import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const PartnerBenefitsPanel = ({ dealerId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/admin/dealers/${dealerId}/partner-benefits`).then((r) => setData(r.data)).catch(() => setData(null)), [dealerId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const grant = async (b) => {
    setBusy(b.id);
    try {
      const r = await api.post(`/admin/dealers/${dealerId}/partner-benefits/${b.id}/grant`, { note: "Granted from dealer pricing tab" });
      toast.success(r.data.idempotent ? "Already granted this period" : `Granted: ${b.title}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(null); }
  };

  if (!data) return null;
  return (
    <Card className="border-border bg-white" data-testid="partner-benefits-panel">
      <CardContent className="p-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-warmgrey">Partner benefits</p>
        {!data.published ? (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="partner-benefits-unpublished">No published Partner Program — configure it under Partner Program.</p>
        ) : (
          <>
            <p className="mt-1 text-xs text-muted-foreground">Program v{data.program_version} · approved tier {data.current_status} · period from {new Date(data.period_start).toLocaleDateString()}</p>
            {data.benefits.length === 0 && <p className="mt-2 text-sm text-muted-foreground" data-testid="partner-benefits-none">No benefits configured for this tier.</p>}
            <ul className="mt-3 divide-y divide-limestone">
              {data.benefits.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`partner-benefit-${b.id}`}>
                  <div>
                    <p className="font-medium text-charcoal">{b.title} <span className="ml-1 text-[10px] uppercase text-warmgrey">{b.tier} · {b.mode}</span></p>
                    <p className="text-xs text-muted-foreground">Used {b.used_this_period}{b.limit_per_period != null ? ` / ${b.limit_per_period}` : ""} this period</p>
                  </div>
                  {b.mode === "manual" && (
                    <Button size="sm" variant="outline" disabled={!b.eligible || busy === b.id} onClick={() => grant(b)} data-testid={`grant-benefit-${b.id}`}>Grant</Button>
                  )}
                </li>
              ))}
            </ul>
            {data.grants.length > 0 && (
              <div className="mt-4" data-testid="partner-benefit-grants">
                <p className="text-[11px] uppercase tracking-wide text-warmgrey">Grant history</p>
                <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                  {data.grants.slice(0, 10).map((g) => <li key={g.id}>{new Date(g.created_at).toLocaleString()} · {g.benefit_title} · {g.mode} · {g.actor}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
